import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { MenuItem } from "../../../core/models/menu-item";
import { SortByKeyPipe } from "../../../core/pipes/sort-by-key.pipe";
import { ContextMenuService } from "../../../core/services/context-menu.service";
import { MaterialModule } from "../../../core/shared/material.module";
import { SharedComponents } from "../../shared.module";

/**
 * Component representing an individual item within the side navigation menu.
 * Handles toggling the expanded state of the menu item and emitting events when an item is clicked.
 */
@Component({
  selector: "app-side-nav-item",
  standalone: true,
  imports: [...SharedComponents, CommonModule, SortByKeyPipe, MaterialModule],
  templateUrl: "./side-nav-item.component.html",
  styleUrl: "./side-nav-item.component.scss",
})
export class SideNavItemComponent {
  [x: string]: any;
  @Input()
  item!: MenuItem;

  @Input()
  index!: number;

  @Input() expandedItems!: Set<string>;
  @Input() selectedItem: MenuItem | null = null;

  @Output() itemSelected = new EventEmitter<MenuItem>();
  @Output() menuClicked = new EventEmitter<any>();

  /**
   * Constructor for the SideNavItemComponent.
   * @param contextMenuService The service to trigger context menu actions.
   */
  constructor(private readonly contextMenuService: ContextMenuService) {}

  /**
   * Toggles the expanded/collapsed state of the current menu item if it has children.
   * Otherwise, selects the item and emits the event.
   */
  toggleExpand() {
    if (this.item.children) {
      if (this.expandedItems.has(this.item.id)) {
        this.expandedItems.delete(this.item.id);
      } else {
        this.expandedItems.add(this.item.id);
      }
    } else if(!this.item.isDraggable) {
      this.onItemClick(this.item);
    }
  }

  /**
   * Handles the selection of a menu item and emits the selection event.
   * @param item The menu item to be selected.
   */
  onItemClick(item: MenuItem) {
    if(item.isDraggable) {
      return;
    }
    this.setSelectedItem(item);
    this.itemSelected.emit(item);
  }

  /**
   * Handles the context menu click event and triggers the appropriate context menu action.
   * @param type The type of context menu action.
   * @param item The menu item that was clicked.
   */
  onMenuClick(type: string, item: MenuItem) {
    this.contextMenuService.triggerMenuItem({
      type: type,
      item,
    });
  }

  /**
   * Sets the selected menu item.
   * @param item The menu item to be selected, or null to deselect.
   */
  setSelectedItem(item: MenuItem | null) {
    this.selectedItem = item;
  }
}
